/**
 * Resolve the client-facing owner of a lead per lifecycle phase.
 *
 * Mirrors the backend ``lead_owner_service`` so the UI, PDFs and emails
 * agree on who the client's contact is at any point of the journey.
 *
 * Phases
 * ------
 * 1. **Atendimento** — from inbound until the RT is scheduled
 * 2. **Consultor de Cuidados** — RT scheduled → deposit paid
 * 3. **Gestor de Cliente** — after deposit reconciled
 */

export const OWNER_PHASE_LABELS = {
  atendimento: "Atendimento",
  consultor:   "Consultor de Cuidados",
  gestor:      "Gestor de Cliente",
};

const POST_DEPOSIT_STATUSES = new Set([
  "aceite", "activo", "servico", "servico_ativo",
]);

function _hasPaidDeposit(lead) {
  const dep = lead?.deposit || {};
  if (dep.paid || dep.reconciled) return true;
  if (POST_DEPOSIT_STATUSES.has(lead?.status)) return true;
  return false;
}

function _hasScheduledRT(lead) {
  return Boolean((lead?.scheduled_qt_at || "").trim());
}

/** Return one of ``"atendimento"``, ``"consultor"``, ``"gestor"``. */
export function resolveOwnerPhase(lead) {
  if (!lead) return "atendimento";
  if (_hasPaidDeposit(lead)) return "gestor";
  if (_hasScheduledRT(lead)
      || lead.assigned_consultor_id
      || (lead.atendimento_closed_at || "").trim()) return "consultor";
  return "atendimento";
}

/** Return an owner descriptor: {phase, roleLabel, name, id}. */
export function getLeadOwner(lead) {
  const phase = resolveOwnerPhase(lead);
  if (phase === "gestor") {
    return {
      phase,
      roleLabel: OWNER_PHASE_LABELS.gestor,
      id:   lead?.assigned_manager_id || null,
      name: lead?.assigned_manager_name || "—",
    };
  }
  if (phase === "consultor") {
    return {
      phase,
      roleLabel: OWNER_PHASE_LABELS.consultor,
      id:   lead?.assigned_consultor_id || lead?.assigned_manager_id || null,
      name: (lead?.assigned_consultor_name
              || lead?.assigned_manager_name
              || "—"),
    };
  }
  return {
    phase,
    roleLabel: OWNER_PHASE_LABELS.atendimento,
    id:   lead?.created_by || null,
    name: lead?.atendimento_name || "Equipa Caring",
  };
}

/** True when the lead already crossed a phase boundary. */
export function isPastPhase(lead, phase) {
  const order = { atendimento: 0, consultor: 1, gestor: 2 };
  return (order[resolveOwnerPhase(lead)] || 0) > (order[phase] || 0);
}
